import { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, PenTool } from 'lucide-react';
import { SignatureCanvas, type SignatureCanvasRef } from '@/components/SignatureCanvas';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tabla: 'partes_trabajo' | 'informes_consolidados';
  documentoId: string;
  numeroDocumento: string;
  tituloDocumento: string;
  nombrePorDefecto?: string;
  onFirmado?: () => void;
}

export function PortalFirmarDialog({
  open,
  onOpenChange,
  tabla,
  documentoId,
  numeroDocumento,
  tituloDocumento,
  nombrePorDefecto = '',
  onFirmado,
}: Props) {
  const sigRef = useRef<SignatureCanvasRef>(null);
  const [nombre, setNombre] = useState(nombrePorDefecto);
  const [aceptado, setAceptado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  async function handleFirmar() {
    const firma = sigRef.current?.getSignature() || '';
    if (!firma) return toast.error('Por favor, firma en el recuadro');
    if (!nombre.trim()) return toast.error('Introduce tu nombre y apellidos');
    if (!aceptado) return toast.error('Debes confirmar la revisión del documento');

    setEnviando(true);
    try {
      const { error } = await (supabase as any)
        .from(tabla)
        .update({
          firma_cliente: firma,
          nombre_firmante_cliente: nombre.trim(),
          fecha_firma_cliente: new Date().toISOString(),
          firma_origen: 'portal',
        })
        .eq('id', documentoId);
      if (error) throw error;
      // Notificar al backoffice (no bloqueante)
      try {
        await supabase.functions.invoke('notificar-firma-documento', {
          body: {
            tipo: tabla === 'partes_trabajo' ? 'parte_trabajo' : 'informe_consolidado',
            documento_id: documentoId,
          },
        });
      } catch (_) { /* ignorar fallo de notificación */ }
      toast.success('Documento firmado correctamente');
      onFirmado?.();
      onOpenChange(false);
      setNombre(nombrePorDefecto);
      setAceptado(false);
      sigRef.current?.clear();
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo firmar el documento');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="h-5 w-5" />
            Firmar {tituloDocumento}
          </DialogTitle>
          <DialogDescription>
            Documento <strong>{numeroDocumento}</strong>. Tu firma quedará registrada con fecha y hora.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="firmante">Nombre y apellidos del firmante</Label>
            <Input
              id="firmante"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre completo"
            />
          </div>

          <SignatureCanvas ref={sigRef} title="Firma con el dedo o el ratón" />

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={aceptado}
              onCheckedChange={(v) => setAceptado(v === true)}
              className="mt-0.5"
            />
            <span>He revisado el documento y confirmo su conformidad.</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={handleFirmar} disabled={enviando}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenTool className="h-4 w-4 mr-2" />}
            Firmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
